import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getWritingTypes } from 'services/escriturasTemplatesService';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { useSnackbar } from 'context/SnackbarContext';
import { useNotificationsRealtime } from 'context/notifications/NotificationsRealtimeContext';
import { escriturasTemplatesI18n } from './i18n';
import TemplateCreateModal from './TemplateCreateModal';
import WritingTypeFormModal from './WritingTypeFormModal';
import '../../styles/naEeppTableGrid.scss';

const FILTER_ALL = '__all__';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

export default function EscriturasTemplates({ DesignSystemComponents }) {
  const { language } = useLanguage();
  const tplLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: escriturasTemplatesI18n });
  const { showSnackbar } = useSnackbar();
  const { subscribeToTemplates } = useNotificationsRealtime();
  const navigate = useNavigate();

  const { Button, Table, Badge, IconButton, Dropdown, Card } =
    DesignSystemComponents ?? {};

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createForType, setCreateForType] = useState(null);
  const [typeForm, setTypeForm] = useState(null); // { mode: 'create'|'edit', writingType }
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // VS-3: filtros + sorting client-side (la lista llega completa).
  const [search, setSearch] = useState('');
  const [familyFilter, setFamilyFilter] = useState(FILTER_ALL);
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const abortRef = useRef(null);

  useEffect(() => {
    escriturasTemplatesI18n.changeLanguage(tplLanguage);
  }, [tplLanguage]);

  const fetchTypes = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await getWritingTypes(controller.signal);
      setTypes(data || []);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(
        err.response?.data?.message || err.message || t('tpl_error_generic')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTypes();
    return () => abortRef.current?.abort();
  }, [fetchTypes]);

  // Tiempo real: refleja "en proceso" para todos. Al iniciar, marca el spinner al instante;
  // al terminar, re-fetch para traer la versión/badge nuevos.
  useEffect(() => {
    const unsubscribe = subscribeToTemplates(({ templateName, generating }) => {
      if (!templateName) return;
      setTypes((prev) =>
        prev.map((r) =>
          r.templateName === templateName || r.code === templateName
            ? { ...r, isGenerating: !!generating }
            : r
        )
      );
      if (!generating) fetchTypes();
    });
    return unsubscribe;
  }, [subscribeToTemplates, fetchTypes]);

  const openDetail = useCallback(
    (row) => {
      const name = row.templateName || row.code;
      navigate(`/escrituras/templates/${encodeURIComponent(name)}`);
    },
    [navigate]
  );

  const handleCreated = useCallback(
    (result) => {
      setCreateForType(null);
      if (result?.template?.version) {
        showSnackbar?.(
          t('tpl_create_success', { version: result.template.version }),
          'success'
        );
      }
      fetchTypes();
    },
    [showSnackbar, t, fetchTypes]
  );

  const handleTypeSaved = useCallback(
    (dto, isEdit) => {
      setTypeForm(null);
      showSnackbar?.(
        isEdit
          ? t('tpl_type_updated')
          : t('tpl_type_created', { name: dto?.displayName }),
        'success'
      );
      fetchTypes();
    },
    [showSnackbar, t, fetchTypes]
  );

  // Solo hay una versión activa por tipo, así que lo útil es saber si la plantilla
  // ya fue generada o no (no qué formato tiene).
  const statusBadge = (row) => {
    const text = row.hasActiveTemplate
      ? t('tpl_generated')
      : t('tpl_not_generated');
    const color = row.hasActiveTemplate ? 'success' : 'light';
    return Badge ? (
      <Badge text={text} color={color} size='sm' />
    ) : (
      <span>{text}</span>
    );
  };

  // Familias distintas presentes (para el filtro). FILTER_ALL = todas.
  const familyOptions = useMemo(() => {
    const families = [
      ...new Set(types.map((tp) => tp.familyJuridica).filter(Boolean)),
    ].sort();
    return [
      { label: t('tpl_filter_all_families'), value: FILTER_ALL },
      ...families.map((f) => ({ label: f, value: f })),
    ];
  }, [types, t]);

  const statusOptions = useMemo(
    () => [
      { label: t('tpl_filter_all_status'), value: FILTER_ALL },
      { label: t('tpl_generated'), value: 'generated' },
      { label: t('tpl_not_generated'), value: 'not_generated' },
    ],
    [t]
  );

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction ?? null);
    setPage(0);
  };

  // Filtrado + orden client-side. El re-fetch trae todo; aquí se acota y ordena.
  const visibleTypes = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = types.filter((tp) => {
      if (q && !`${tp.displayName} ${tp.code}`.toLowerCase().includes(q))
        return false;
      if (
        familyFilter !== FILTER_ALL &&
        (tp.familyJuridica || '') !== familyFilter
      )
        return false;
      if (statusFilter === 'generated' && !tp.hasActiveTemplate) return false;
      if (statusFilter === 'not_generated' && tp.hasActiveTemplate)
        return false;
      return true;
    });
    if (sortKey && sortDirection) {
      const factor = sortDirection === 'desc' ? -1 : 1;
      rows = [...rows].sort((a, b) => {
        let av = a[sortKey];
        let bv = b[sortKey];
        if (sortKey === 'updatedAt') {
          av = av ? new Date(av).getTime() : 0;
          bv = bv ? new Date(bv).getTime() : 0;
        }
        if (typeof av === 'string' || typeof bv === 'string')
          return String(av ?? '').localeCompare(String(bv ?? '')) * factor;
        return ((av ?? 0) - (bv ?? 0)) * factor;
      });
    }
    return rows;
  }, [types, search, familyFilter, statusFilter, sortKey, sortDirection]);

  const columns = [
    { accessor: 'displayName', label: t('tpl_col_type'), sort: true },
    { accessor: 'code', label: 'Code', sort: true },
    {
      accessor: 'familyJuridica',
      label: t('tpl_col_family'),
      sort: true,
      render: (r) => r.familyJuridica || '—',
    },
    {
      accessor: 'tiempos',
      label: t('tpl_col_tiempos'),
      sort: true,
      render: (r) => (r.tiempos == null ? '—' : r.tiempos),
    },
    {
      accessor: 'activeVersion',
      label: t('tpl_col_template'),
      sort: true,
      render: (r) =>
        r.hasActiveTemplate
          ? t('tpl_has_template', { version: r.activeVersion })
          : '—',
    },
    {
      accessor: 'hasActiveTemplate',
      label: t('tpl_col_status'),
      sort: true,
      render: statusBadge,
    },
    {
      accessor: 'updatedAt',
      label: t('tpl_col_updated_at'),
      sort: true,
      render: (r) => formatDate(r.updatedAt),
    },
    {
      label: t('tpl_col_actions'),
      render: (r) => {
        // En proceso (por cualquier usuario): spinner en vez de acciones. "Ver" (detalle)
        // queda disponible siempre — el detalle es el hub del tipo (secciones + documentos).
        if (r.isGenerating) {
          return (
            <div className='d-flex gap-2 align-items-center'>
              <span
                className='d-flex align-items-center gap-1'
                style={{ fontSize: 12, color: '#64748B' }}
              >
                <span
                  className='spinner-border spinner-border-sm text-primary'
                  role='status'
                  aria-hidden='true'
                />
                {t('tpl_generating')}
              </span>
              {IconButton ? (
                <IconButton
                  ariaLabel={t('tpl_action_view')}
                  icon='eye'
                  size='sm'
                  stroke='var(--primary-500)'
                  onClick={() => openDetail(r)}
                />
              ) : (
                <button
                  type='button'
                  className='btn btn-link btn-sm'
                  onClick={() => openDetail(r)}
                >
                  {t('tpl_action_view')}
                </button>
              )}
            </div>
          );
        }
        return (
          <div className='d-flex gap-2 align-items-center'>
            {IconButton ? (
              <IconButton
                ariaLabel={t('tpl_action_view')}
                icon='eye'
                size='sm'
                stroke='var(--primary-500)'
                onClick={() => openDetail(r)}
              />
            ) : (
              <button
                type='button'
                className='btn btn-link btn-sm'
                onClick={() => openDetail(r)}
              >
                {t('tpl_action_view')}
              </button>
            )}
            {/* Crear (sin plantilla) o Regenerar (con plantilla): ambos abren el mismo
                modal de generación. "Regenerar" salió del menú ⋮ al quitar "Desactivar". */}
            {Button ? (
              <Button
                label={
                  r.hasActiveTemplate
                    ? t('tpl_action_regenerate')
                    : t('tpl_action_create')
                }
                size='sm'
                variant={r.hasActiveTemplate ? 'naked' : 'primary'}
                onClick={() => setCreateForType(r)}
              />
            ) : (
              <button
                type='button'
                className={
                  r.hasActiveTemplate
                    ? 'btn btn-link btn-sm'
                    : 'btn btn-primary btn-sm'
                }
                onClick={() => setCreateForType(r)}
              >
                {r.hasActiveTemplate
                  ? t('tpl_action_regenerate')
                  : t('tpl_action_create')}
              </button>
            )}
            {/* Editar: el ⋮ quedaba con solo "editar" (Desactivar se movió al form como
                campo Estado) → IconButton pencil. */}
            {IconButton ? (
              <IconButton
                ariaLabel={t('tpl_type_action_edit')}
                icon='pencil'
                size='sm'
                stroke='var(--primary-500)'
                onClick={() => setTypeForm({ mode: 'edit', writingType: r })}
              />
            ) : (
              <button
                type='button'
                className='btn btn-link btn-sm'
                onClick={() => setTypeForm({ mode: 'edit', writingType: r })}
              >
                {t('tpl_type_action_edit')}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const ContentWrapper = Card || 'div';
  const contentWrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('tpl_title')}</h2>
          <h4 className='fw-normal'>{t('tpl_subtitle')}</h4>
        </div>
        <div className='d-flex gap-2 align-items-center'>
          {Button && (
            <Button
              label={t('tpl_type_create_btn')}
              size='sm'
              variant='primary'
              onClick={() => setTypeForm({ mode: 'create', writingType: null })}
            />
          )}
          {IconButton && (
            <IconButton
              ariaLabel={t('tpl_refresh')}
              icon='arrowCounterclockwise'
              size='lg'
              iconFill='var(--primary-500)'
              onClick={fetchTypes}
              disabled={loading}
            />
          )}
        </div>
      </div>

      <ContentWrapper {...contentWrapperProps}>
        {error && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Filtros: búsqueda (nombre/code) + familia + estado */}
        <div className='d-flex flex-wrap gap-2 mb-3' style={{ maxWidth: 720 }}>
          {DesignSystemComponents?.Input && (
            <div style={{ flex: '1 1 220px' }}>
              <DesignSystemComponents.Input
                icon='search'
                placeholder={t('tpl_filter_search_placeholder')}
                value={search}
                onChange={(e) => {
                  setSearch(e?.target ? e.target.value : e);
                  setPage(0);
                }}
                width='100%'
              />
            </div>
          )}
          {Dropdown && (
            <div style={{ flex: '0 1 200px' }}>
              <Dropdown
                options={familyOptions}
                value={
                  familyOptions.find((o) => o.value === familyFilter) ||
                  familyOptions[0]
                }
                onChange={(o) => {
                  setFamilyFilter(o?.value ?? FILTER_ALL);
                  setPage(0);
                }}
                width='100%'
              />
            </div>
          )}
          {Dropdown && (
            <div style={{ flex: '0 1 200px' }}>
              <Dropdown
                options={statusOptions}
                value={
                  statusOptions.find((o) => o.value === statusFilter) ||
                  statusOptions[0]
                }
                onChange={(o) => {
                  setStatusFilter(o?.value ?? FILTER_ALL);
                  setPage(0);
                }}
                width='100%'
              />
            </div>
          )}
        </div>

        <div className='table-container na-eepp-grid-8'>
          {Table ? (
            <Table
              columns={columns}
              data={visibleTypes}
              loading={loading}
              paginator
              paginationMode='client'
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(0);
              }}
              totalItems={visibleTypes.length}
              paginatorGroup={[
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
              ]}
              serverSortKey={sortKey}
              serverSortDirection={sortDirection}
              onServerSortChange={handleServerSortChange}
            />
          ) : (
            <p className='font-light'>{loading ? t('tpl_loading') : null}</p>
          )}
          {!loading && visibleTypes.length === 0 && (
            <div className='text-center p-4'>
              <p className='caption font-light'>{t('tpl_no_types')}</p>
            </div>
          )}
        </div>

        {createForType && (
          <TemplateCreateModal
            DesignSystemComponents={DesignSystemComponents}
            writingType={createForType}
            t={t}
            onClose={() => setCreateForType(null)}
            onCreated={handleCreated}
          />
        )}

        {typeForm && (
          <WritingTypeFormModal
            DesignSystemComponents={DesignSystemComponents}
            mode={typeForm.mode}
            writingType={typeForm.writingType}
            t={t}
            onClose={() => setTypeForm(null)}
            onSaved={handleTypeSaved}
          />
        )}
      </ContentWrapper>
    </div>
  );
}
